import { type ReactElement, useCallback, useEffect, useState } from 'react';

import { default as Button } from '~components/generic/elements/Button';
import { default as Loader } from '~components/generic/loader/Loader';
import { Spinner } from '~components/generic/spinner/Spinner';
import {
  createApiMediaUploadCredentials,
  createJsmRequest,
  loadJsmWidgetSettings,
  loadJsmWidgetSettingsByRequestType,
} from '~modules/jsm-contact/shared/helpers';
import {
  type ApiMediaUploadCredentials,
  type ApiMediaUploadSettings,
  type BlockFormData,
  type FieldSettings,
  type FieldValue,
  type FormError,
  type FormSettings,
} from '~modules/jsm-contact/shared/types';
import { FieldGroup } from './FieldGroup';

type ContactFormProps = {
  dataKey: string;
  portalBaseUrl?: string;
  requestTypeId?: string;
  defaultFormData?: BlockFormData;
};

export const ContactForm = (props: ContactFormProps): ReactElement => {
  const [formSettings, setFormSettings] = useState<FormSettings>();
  const [formData, setFormData] = useState<BlockFormData>(props.defaultFormData ?? undefined);
  const [formError, setFormError] = useState<FormError>();
  const [apiMediaUploadSettings, setApiMediaUploadSettings] = useState<ApiMediaUploadSettings>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isComplete, setIsComplete] = useState<boolean>(false);
  // to avoid multiple calls to useEffect, when the component is rendered a couple of times, cache the component props in his state
  const [dataKey] = useState<string>(props.dataKey);
  const [portalBaseUrl] = useState<string | undefined>(props.portalBaseUrl);
  const [requestTypeId] = useState<string | undefined>(props.requestTypeId);

  const loadApiMediaUploadSettings = (portalBaseUrl?: string): void => {
    if (portalBaseUrl) {
      createApiMediaUploadCredentials(portalBaseUrl)
        .then((res: ApiMediaUploadCredentials) => {
          setApiMediaUploadSettings({ ...res, portalBaseUrl });
          const delta = res.expiryEpoch - Date.now();
          if (delta > 0) {
            // refresh token in delta seconds and return cleanup function
            const timer = setTimeout(() => loadApiMediaUploadSettings(portalBaseUrl), delta);
            return () => clearTimeout(timer);
          }
        })
        .catch((error: any) => {
          setIsLoading(false);
          setFormError(error as FormError);
        });
    }
  };

  useEffect(() => {
    // reset first
    setFormSettings(undefined);
    setFormData(props.defaultFormData ?? undefined);
    setFormError(undefined);
    setApiMediaUploadSettings(undefined);

    if (!dataKey) {
      return;
    }

    setIsLoading(true);
    const promise = requestTypeId
      ? loadJsmWidgetSettingsByRequestType(dataKey, requestTypeId)
      : loadJsmWidgetSettings(dataKey);
    promise
      .then((res: FormSettings) => {
        setFormSettings(res);
        setIsLoading(false);
      })
      .catch((error: any) => {
        setIsLoading(false);
        setFormError(error as FormError);
      });

    return loadApiMediaUploadSettings(portalBaseUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataKey, requestTypeId, portalBaseUrl]);

  useEffect(() => {
    if (!(formSettings && isSubmitting)) {
      return;
    }
    console.debug('ContactForm: formData (useEffect):', formData);

    // if any upload field contains the undefined value, it means, upload for the corresponding file is still running
    const unfinishedUploadFields = formSettings.fields.filter((fieldSettings: FieldSettings) => {
      return (
        fieldSettings.type === 'file' &&
        formData &&
        formData[fieldSettings.name] !== undefined &&
        (formData[fieldSettings.name] as FieldValue[]).includes(undefined)
      );
    });
    console.debug('ContactForm: unfinishedUploadFields (useEffect):', unfinishedUploadFields);

    // if any upload field contains the null value, it means, upload for the corresponding file has failed
    const failedUploadFields = formSettings.fields.filter((fieldSettings: FieldSettings) => {
      return (
        fieldSettings.type === 'file' &&
        formData &&
        formData[fieldSettings.name] !== undefined &&
        (formData[fieldSettings.name] as FieldValue[]).includes(null)
      );
    });
    console.debug('ContactForm: failedUploadFields (useEffect):', failedUploadFields);

    if (failedUploadFields.length === 0) {
      if (unfinishedUploadFields.length === 0 && !isLoading) {
        setIsLoading(true);
        createJsmRequest(formData, formSettings, dataKey)
          .then(() => {
            // ticket was created
            setIsSubmitting(false);
            setIsComplete(true);
            setIsLoading(false);
            setFormData(props.defaultFormData ?? undefined);
            setFormError(undefined);
          })
          .catch((error: any) => {
            setIsSubmitting(false);
            setIsLoading(false);
            setFormError(error as FormError);
          });
      }
    } else {
      setIsSubmitting(false);
    }
  }, [formSettings, formData, isSubmitting, isLoading, dataKey, props.defaultFormData]);

  const onSubmit = () => {
    if (!formSettings) {
      return;
    }

    setFormError(undefined);
    // setting isSubmitting to true triggers uploading of selected files first
    setIsSubmitting(true);
  };

  const onError = () => {
    setIsSubmitting(false);
  };

  const onReload = () => {
    setIsComplete(false);
  };

  const onValueChanged = useCallback((key: string, value: FieldValue | FieldValue[]) => {
    setFormData(currentData => {
      console.debug('ContactForm: formData (onValueChanged):', { ...currentData, [key]: value });
      return { ...currentData, [key]: value };
    });
  }, []);

  if (isComplete) {
    return (
      <div className="jsm-contact container relative px-0">
        <h3>Many thanks for your request!</h3>
        <p className="my-5 pr-8">
          {formSettings?.config.confirmationText ??
            'You will soon receive a confirmation via email acknowledging the receipt of your inquiry. Through the link provided in the email, you can check the status of your ticket at any time in the ticket portal.'}
        </p>
        <Button
          className={'c-button mt-3 c-button--dark u-padding-horizontal-lg'}
          // aspect="default"
          // emphasis="default"
          onClick={onReload}
          // rounding="medium"
          // size="medium"
          type="button"
          // variant="default"
        >
          Start New Request
        </Button>
      </div>
    );
  }

  if (!formSettings && isLoading) {
    return <Loader size="md" />;
  }

  return (
    <div className="jsm-contact container relative px-0">
      {formError !== undefined && formError.fieldName === null && (
        <div className="error c-hint--error text-sm">{formError.message}</div>
      )}

      <p className="u-margin-vertical-lg">
        {formSettings?.description.replace(/\*\.?$/, '')}
        {formSettings?.description.match(/\*\.?$/) && <span className="c-hint--error">*</span>}
      </p>

      <div className="form-group">
        {formSettings &&
          formSettings.fields.map((fieldSettings: FieldSettings) => {
            const fieldValue = formData ? formData[fieldSettings.name] : undefined;
            const fieldError =
              formError !== undefined && formError.fieldName === fieldSettings.name ? formError.message : null;

            return (
              <FieldGroup
                key={fieldSettings.name}
                fieldSettings={fieldSettings}
                fieldValue={fieldValue}
                error={fieldError}
                onValueChanged={onValueChanged}
                onError={onError}
                isSubmitting={isSubmitting}
                dataKey={dataKey}
                apiMediaUploadSettings={apiMediaUploadSettings}
              />
            );
          })}

        {formSettings && (
          <Button
            className={'c-button mt-3 c-button--dark u-padding-horizontal-lg'}
            // aspect="default"
            // emphasis="default"
            onClick={onSubmit}
            // rounding="medium"
            // size="medium"
            type="submit"
            // variant="default"
            disabled={isSubmitting}
          >
            {isLoading && <Spinner className="u-margin-horizontal-sm" />}
            Send
          </Button>
        )}
        {formError && formSettings && (
          <div className="error c-hint--error text-sm pt-2 hidden">Error has occurred.</div>
        )}
      </div>
    </div>
  );
};
